import { useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  ChevronDown,
  ChevronUp,
  Edit,
  FileJson,
  FileText,
  GitPullRequest,
  Globe,
  Link2,
  Plus,
  RefreshCw,
  Send,
  Trash2,
  X,
} from 'lucide-react';
import type { Approval, ApprovalRequest, DestructiveOperation } from '../api/approvalRequests';
import {
  contentApi,
  type ContentItem,
  type ContentRevision,
  type ContentUpdates,
} from '../api/content';
import ApprovalRequestModal from '../components/ApprovalRequestModal';
import { useAuth } from '../components/AuthProvider';
import { StatusChip, useToast } from '../components/ui';
import { approvalRequestKeys, useApprovalRequests } from '../hooks/useApprovalRequests';
import {
  canSetCurrent,
  carryUneditedProposalFields,
  editBlockedBy,
  proposalToUpdates,
  revisionRowState,
  type RevisionRowState,
} from './contentRevisions.logic';

const CONTENT_TYPE_PRESETS = [
  'TAX_INFO',
  'CAUSE_CATEGORIES',
  'SCREEN_COPY',
  'ANNOUNCEMENT',
  'FAQ',
  'FEATURE_FLAG',
  'LEGAL',
  'ONBOARDING',
];

const STATUS_COLORS: Record<string, string> = {
  DRAFT: 'bg-gray-500/20 text-gray-400',
  PUBLISHED: 'bg-green-500/20 text-green-400',
  ARCHIVED: 'bg-red-500/20 text-red-400',
};

const DEFAULT_FIELDS = '{\n  "title": ""\n}';

const CAUSE_CATEGORY_FIELDS = JSON.stringify(
  {
    content_version: 1,
    last_updated: new Date().toISOString(),
    cause_categories: {
      label: 'Cause Category',
      required: true,
      options: [{ code: 'Q', label: 'International Foreign Affairs' }],
    },
  },
  null,
  2
);

type RouteMode = 'CURRENT' | 'VERSIONED';

/**
 * DM-6437: publish, set-current and delete are FM-105 destructive operations.
 * Each opens the shared approval modal, which runs `execute` with the reason
 * + token once a second operator has approved.
 *
 * DM-7214: an edit to a live route is not one of them any more. It is stored
 * on the server as a proposed revision (`openRequest`), and publishing it
 * needs nothing this page remembers: the modal is resumed from the request id
 * the server recorded, and `execute` publishes by the ids on that request.
 */
interface PendingApproval {
  operation: DestructiveOperation;
  targetId: string;
  targetLabel: string;
  title: string;
  description?: string;
  execute: (approval: Approval, request: ApprovalRequest) => Promise<void>;
  openRequest?: (reason: string, note: string | undefined) => Promise<ApprovalRequest>;
  resumeRequestId?: string | null;
  stale?: boolean;
  onRevise?: () => void;
}

/** Publishes what the server stored for the request. Sends no content. */
async function publishApprovedRevision(
  approval: Approval,
  request: ApprovalRequest
): Promise<void> {
  if (!request.revision) {
    throw new Error('This request does not point at a stored revision.');
  }
  await contentApi.publishRevision(
    request.revision.content_id,
    request.revision.revision_id,
    approval
  );
}

function normalizeRoutePath(value: string): string {
  return value
    .trim()
    .toLowerCase()
    .replace(/^\/+|\/+$/g, '')
    .replace(/[^a-z0-9/_-]+/g, '-')
    .replace(/\/{2,}/g, '/')
    .replace(/-{2,}/g, '-');
}

function normalizeContentType(value: string): string {
  return value
    .trim()
    .toUpperCase()
    .replace(/[^A-Z0-9_]+/g, '_')
    .replace(/_+/g, '_')
    .replace(/^_+|_+$/g, '');
}

function parseJson(value: string): Record<string, unknown> | null {
  try {
    const parsed = JSON.parse(value);
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

function getRoutePath(item: ContentItem): string {
  return item.route_path || normalizeRoutePath(item.key);
}

function getVersionQueryParams(value: string): string[] {
  return value
    .split(',')
    .map(item => item.trim())
    .filter(Boolean);
}

export default function DynamicContent() {
  const queryClient = useQueryClient();
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [typeFilter, setTypeFilter] = useState('');
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [editingItem, setEditingItem] = useState<ContentItem | null>(null);
  const [formError, setFormError] = useState('');

  const [formDisplayName, setFormDisplayName] = useState('');
  const [formType, setFormType] = useState('TAX_INFO');
  const [formRoutePath, setFormRoutePath] = useState('tax-info');
  const [formLocale, setFormLocale] = useState('en');
  const [formMode, setFormMode] = useState<RouteMode>('VERSIONED');
  const [formRouteVersion, setFormRouteVersion] = useState('2025');
  const [formVersionQueryParams, setFormVersionQueryParams] = useState('version, year');
  const [formFields, setFormFields] = useState(DEFAULT_FIELDS);

  const [editDisplayName, setEditDisplayName] = useState('');
  const [editRoutePath, setEditRoutePath] = useState('');
  const [editMode, setEditMode] = useState<RouteMode>('CURRENT');
  const [editRouteVersion, setEditRouteVersion] = useState('');
  const [editVersionQueryParams, setEditVersionQueryParams] = useState('version');
  const [editFields, setEditFields] = useState('{}');
  /** Set while the editor holds the text of a stored revision rather than the live item. */
  const [editingRevisionNote, setEditingRevisionNote] = useState<string | null>(null);
  /** The stored proposal being revised: it carries settings this editor has no inputs for. */
  const [editingProposal, setEditingProposal] = useState<Pick<
    ContentRevision,
    'proposal' | 'change_note'
  > | null>(null);

  const { user } = useAuth();
  const approvalRequests = useApprovalRequests();

  const {
    data: items = [],
    isLoading,
    refetch,
  } = useQuery({
    queryKey: ['content', typeFilter],
    queryFn: () => contentApi.list(typeFilter || undefined),
  });

  const contentTypes = useMemo(() => {
    const values = new Set(CONTENT_TYPE_PRESETS);
    for (const item of items) {
      if (item.content_type) values.add(item.content_type);
    }
    return [...values].sort();
  }, [items]);

  const toast = useToast();
  const [approvalAction, setApprovalAction] = useState<PendingApproval | null>(null);

  function rowState(item: ContentItem): RevisionRowState | null {
    return revisionRowState({
      item,
      requests: approvalRequests.data ?? [],
      viewerSub: user?.sub,
      nowIso: new Date().toISOString(),
    });
  }

  function refreshRevisions(): void {
    queryClient.invalidateQueries({ queryKey: ['content'] });
    queryClient.invalidateQueries({ queryKey: approvalRequestKeys.all });
  }

  /** Re-propose the text the server already holds: a new request, nothing retyped. */
  function reproposeStored(item: ContentItem) {
    return async (reason: string, note: string | undefined): Promise<ApprovalRequest> => {
      const summary = item.pending_revision;
      if (!summary) throw new Error('This route has no stored revision any more.');
      const detail = await contentApi.getRevision(item.id, summary.revision_id);
      const proposed = await contentApi.proposeRevision(
        item.id,
        proposalToUpdates(detail.revision.proposal, detail.revision.change_note),
        reason,
        note
      );
      return proposed.request;
    };
  }

  function openRevision(item: ContentItem, state: RevisionRowState): void {
    if (state.action === 'revise' || state.action === 'rebase') {
      void openEditFromRevision(item);
      return;
    }
    const summary = item.pending_revision;
    if (!summary) return;
    const publicPath = `/content/${getRoutePath(item)}`;
    setApprovalAction({
      operation: 'publishContentRevision',
      targetId: item.id,
      targetLabel: publicPath,
      title:
        state.action === 'publish'
          ? `Publish revision of ${publicPath}`
          : `Revision of ${publicPath}`,
      description: state.note,
      execute: publishApprovedRevision,
      resumeRequestId: state.action === 'rerequest' ? null : summary.request_id,
      stale: summary.stale,
      openRequest: state.mine ? reproposeStored(item) : undefined,
      onRevise: state.mine
        ? () => {
            setApprovalAction(null);
            void openEditFromRevision(item);
          }
        : undefined,
    });
  }

  async function openEditFromRevision(item: ContentItem): Promise<void> {
    const summary = item.pending_revision;
    if (!summary) return;
    try {
      const { revision, stale } = await contentApi.getRevision(item.id, summary.revision_id);
      openEdit({ ...item, ...revision.proposal }, true);
      setEditingProposal(revision);
      setEditingRevisionNote(
        stale
          ? 'This is your stored revision, reopened on the current live version. Check it against what is live now; proposing it again replaces the stale one.'
          : 'This is your stored revision. Proposing it again replaces it and resets its approval.'
      );
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Could not load the stored revision');
      refreshRevisions();
    }
  }

  async function discardRevision(item: ContentItem): Promise<void> {
    const summary = item.pending_revision;
    if (!summary) return;
    const publicPath = `/content/${getRoutePath(item)}`;
    if (
      !window.confirm(
        `Discard the proposed revision of ${publicPath}? The live route is not affected.`
      )
    ) {
      return;
    }
    try {
      await contentApi.discardRevision(item.id, summary.revision_id);
      toast.success(`Discarded the proposed revision of ${publicPath}`);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Could not discard the revision');
    }
    refreshRevisions();
  }

  function requestPublish(item: ContentItem): void {
    const publicPath = `/content/${getRoutePath(item)}`;
    const settingCurrent = item.status === 'PUBLISHED';
    setApprovalAction({
      operation: 'publishContent',
      targetId: item.id,
      targetLabel: publicPath,
      title: settingCurrent ? `Set current: ${publicPath}` : `Publish ${publicPath}`,
      description: settingCurrent
        ? 'Makes this item the version the app reads for the route.'
        : 'Goes live for every app on its next cold start.',
      execute: async approval => {
        await contentApi.publish(item.id, approval);
      },
    });
  }

  function requestDelete(item: ContentItem): void {
    const publicPath = `/content/${getRoutePath(item)}`;
    setApprovalAction({
      operation: 'deleteContent',
      targetId: item.id,
      targetLabel: publicPath,
      title: `Delete ${publicPath}`,
      description:
        item.status === 'PUBLISHED'
          ? 'The route is live; deleting it takes it offline for every app.'
          : undefined,
      execute: async approval => {
        await contentApi.remove(item.id, approval);
      },
    });
  }

  const createMutation = useMutation({
    mutationFn: contentApi.create,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['content'] });
      setShowCreateModal(false);
      setFormError('');
      setFormDisplayName('');
      setFormRoutePath('');
      setFormRouteVersion('');
      setFormFields(DEFAULT_FIELDS);
    },
    onError: error => setFormError(error instanceof Error ? error.message : 'Create failed'),
  });

  const updateMutation = useMutation({
    mutationFn: ({
      id,
      updates,
    }: {
      id: string;
      updates: Parameters<typeof contentApi.update>[1];
    }) => contentApi.update(id, updates),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['content'] });
      setEditingItem(null);
      setFormError('');
    },
    onError: error => setFormError(error instanceof Error ? error.message : 'Update failed'),
  });

  const createRoutePath = normalizeRoutePath(formRoutePath);
  const createUrl =
    formMode === 'VERSIONED' && formRouteVersion
      ? `/content/${createRoutePath}?${getVersionQueryParams(formVersionQueryParams)[0] || 'version'}=${formRouteVersion}`
      : `/content/${createRoutePath}`;

  function selectPreset(value: string): void {
    const contentType = normalizeContentType(value);
    setFormType(contentType);

    if (contentType === 'TAX_INFO') {
      setFormRoutePath('tax-info');
      setFormDisplayName('Tax Info');
      setFormMode('VERSIONED');
      setFormVersionQueryParams('version, year');
    } else if (contentType === 'CAUSE_CATEGORIES') {
      setFormRoutePath('cause-categories');
      setFormDisplayName('Cause Categories');
      setFormMode('CURRENT');
      setFormRouteVersion('');
      setFormVersionQueryParams('version');
      setFormFields(CAUSE_CATEGORY_FIELDS);
    }
  }

  function handleCreate(): void {
    const fields = parseJson(formFields);
    if (!fields) {
      setFormError('Fields must be a JSON object.');
      return;
    }

    if (!createRoutePath) {
      setFormError('Public path is required.');
      return;
    }

    if (formMode === 'VERSIONED' && !formRouteVersion.trim()) {
      setFormError('Version is required for versioned routes.');
      return;
    }

    createMutation.mutate({
      contentType: normalizeContentType(formType),
      key: createRoutePath,
      locale: formLocale,
      routeDisplayName: formDisplayName.trim() || undefined,
      routePath: createRoutePath,
      routeMode: formMode,
      routeVersion: formMode === 'VERSIONED' ? formRouteVersion.trim() : undefined,
      versionQueryParams:
        formMode === 'VERSIONED' ? getVersionQueryParams(formVersionQueryParams) : undefined,
      fields,
    });
  }

  function openEdit(item: ContentItem, fromRevision = false): void {
    if (!fromRevision) {
      const state = rowState(item);
      const blocked = editBlockedBy(state);
      if (blocked) {
        toast.error(blocked);
        return;
      }
      // My own stored revision is what I was editing; start from that text.
      if (state?.mine) {
        void openEditFromRevision(item);
        return;
      }
      setEditingRevisionNote(null);
      setEditingProposal(null);
    }
    setEditingItem(item);
    setFormError('');
    setEditDisplayName(item.route_display_name || '');
    setEditRoutePath(getRoutePath(item));
    setEditMode(item.route_mode || 'CURRENT');
    setEditRouteVersion(item.route_version || '');
    setEditVersionQueryParams((item.version_query_params || ['version']).join(', '));
    setEditFields(JSON.stringify(item.fields || {}, null, 2));
  }

  function handleUpdate(): void {
    if (!editingItem) return;

    const fields = parseJson(editFields);
    if (!fields) {
      setFormError('Fields must be a JSON object.');
      return;
    }

    const routePath = normalizeRoutePath(editRoutePath);
    if (!routePath) {
      setFormError('Public path is required.');
      return;
    }

    if (editMode === 'VERSIONED' && !editRouteVersion.trim()) {
      setFormError('Version is required for versioned routes.');
      return;
    }

    const updates: ContentUpdates = {
      routeDisplayName: editDisplayName.trim() || undefined,
      routePath,
      routeMode: editMode,
      routeVersion: editMode === 'VERSIONED' ? editRouteVersion.trim() : undefined,
      versionQueryParams:
        editMode === 'VERSIONED' ? getVersionQueryParams(editVersionQueryParams) : undefined,
      fields,
    };

    // DM-7214: a live route is never edited in place. The edit goes to the
    // server as a proposed revision together with its approval request, so
    // from that moment it survives a reload, another device and the wait for
    // a second operator. Publishing it later sends no content at all.
    if (editingItem.status === 'PUBLISHED') {
      const target = editingItem;
      const storedProposal = editingProposal;
      setApprovalAction({
        operation: 'publishContentRevision',
        targetId: target.id,
        targetLabel: `/content/${routePath}`,
        title: `Propose revision of /content/${routePath}`,
        description:
          'This route is live. Your edit is stored on the server as a proposed revision and the live route keeps serving the current version. A second operator reviews the exact diff; once they approve, you publish it, from this window or any later session.',
        resumeRequestId: null,
        execute: publishApprovedRevision,
        openRequest: async (reason, note) => {
          const proposed = await contentApi.proposeRevision(
            target.id,
            carryUneditedProposalFields(updates, storedProposal),
            reason,
            note
          );
          // The text is on the server now; the editor has nothing left to lose.
          setEditingItem(null);
          setEditingRevisionNote(null);
          setEditingProposal(null);
          return proposed.request;
        },
      });
      return;
    }

    updateMutation.mutate({ id: editingItem.id, updates });
  }

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
        <div>
          <h1 className="text-3xl font-bold text-white mb-2">Dynamic Content</h1>
          <p className="text-gray-text">Admin-managed routes served from /content/*</p>
        </div>
        <div className="flex items-center space-x-3">
          <button onClick={() => refetch()} className="btn-secondary flex items-center space-x-2">
            <RefreshCw className="w-4 h-4" />
            <span>Refresh</span>
          </button>
          <button
            onClick={() => {
              setFormError('');
              setShowCreateModal(true);
            }}
            className="btn-primary flex items-center space-x-2"
          >
            <Plus className="w-4 h-4" />
            <span>New Route</span>
          </button>
        </div>
      </div>

      <div className="flex flex-wrap gap-2">
        <button
          onClick={() => setTypeFilter('')}
          className={`px-3 py-1.5 rounded-lg text-sm transition-colors ${
            !typeFilter
              ? 'bg-primary text-white'
              : 'bg-dark-lighter text-gray-text hover:text-white'
          }`}
        >
          All
        </button>
        {contentTypes.map(contentType => (
          <button
            key={contentType}
            onClick={() => setTypeFilter(typeFilter === contentType ? '' : contentType)}
            className={`px-3 py-1.5 rounded-lg text-sm transition-colors ${
              typeFilter === contentType
                ? 'bg-primary text-white'
                : 'bg-dark-lighter text-gray-text hover:text-white'
            }`}
          >
            {contentType}
          </button>
        ))}
      </div>

      <div className="glass-card overflow-hidden">
        {isLoading ? (
          <div className="p-12 text-center">
            <RefreshCw className="w-8 h-8 text-primary animate-spin mx-auto mb-4" />
            <p className="text-gray-text">Loading content...</p>
          </div>
        ) : items.length === 0 ? (
          <div className="p-12 text-center">
            <FileText className="w-12 h-12 text-gray-text mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-white mb-2">No content routes</h3>
            <button onClick={() => setShowCreateModal(true)} className="btn-primary">
              Create Route
            </button>
          </div>
        ) : (
          <div className="divide-y divide-gray-border">
            {items.map(item => {
              const routePath = getRoutePath(item);
              const publicPath = `/content/${routePath}`;
              const versionText =
                item.route_mode === 'VERSIONED' && item.route_version
                  ? `version ${item.route_version}`
                  : `content v${item.version}`;
              const revision = rowState(item);

              return (
                <div key={item.id} className="p-6">
                  <div className="flex flex-col gap-4 xl:flex-row xl:items-center xl:justify-between">
                    <div className="flex items-start space-x-4">
                      <Link2 className="w-5 h-5 text-primary mt-1" />
                      <div>
                        <div className="flex flex-wrap items-center gap-2">
                          <h3 className="text-white font-medium font-mono">{publicPath}</h3>
                          <span className="px-2 py-0.5 bg-blue-500/20 text-blue-400 rounded text-xs">
                            {item.content_type}
                          </span>
                          <span className="px-2 py-0.5 bg-purple-500/20 text-purple-300 rounded text-xs">
                            {item.route_mode || 'CURRENT'}
                          </span>
                        </div>
                        <div className="flex flex-wrap items-center gap-3 mt-1 text-sm text-gray-text">
                          <span className="flex items-center space-x-1">
                            <Globe className="w-3 h-3" />
                            <span>{item.locale}</span>
                          </span>
                          <span>{versionText}</span>
                          {item.route_display_name && <span>{item.route_display_name}</span>}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center space-x-3">
                      <span
                        className={`px-3 py-1 rounded-full text-xs font-medium ${STATUS_COLORS[item.status]}`}
                      >
                        {item.status}
                      </span>
                      {revision && (
                        <StatusChip label={revision.chip.label} tone={revision.chip.tone} />
                      )}
                      <div className="flex items-center space-x-1">
                        {canSetCurrent(item) && (
                          <button
                            onClick={() => requestPublish(item)}
                            className="p-2 text-green-400 hover:bg-green-400/10 rounded-lg transition-colors"
                            title={item.status === 'PUBLISHED' ? 'Set current' : 'Publish'}
                          >
                            <Send className="w-4 h-4" />
                          </button>
                        )}
                        <button
                          onClick={() => openEdit(item)}
                          className="p-2 text-gray-text hover:text-primary rounded-lg transition-colors"
                          title="Edit"
                        >
                          <Edit className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => requestDelete(item)}
                          className="p-2 text-gray-text hover:text-red-400 rounded-lg transition-colors"
                          title="Delete"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => setExpandedId(expandedId === item.id ? null : item.id)}
                          className="p-2 text-gray-text hover:text-white rounded-lg transition-colors"
                          title="Toggle details"
                        >
                          {expandedId === item.id ? (
                            <ChevronUp className="w-4 h-4" />
                          ) : (
                            <ChevronDown className="w-4 h-4" />
                          )}
                        </button>
                      </div>
                    </div>
                  </div>

                  {revision && (
                    <div className="mt-4 flex flex-col gap-3 rounded-lg border border-gray-border bg-dark-lighter px-4 py-3 md:flex-row md:items-center md:justify-between">
                      <div className="flex items-start gap-3 text-sm">
                        <GitPullRequest className="w-4 h-4 text-primary flex-shrink-0 mt-0.5" />
                        <p className="text-gray-text">{revision.note}</p>
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        {revision.canDiscard && (
                          <button
                            onClick={() => void discardRevision(item)}
                            className="btn-secondary text-sm"
                          >
                            Discard
                          </button>
                        )}
                        <button
                          onClick={() => openRevision(item, revision)}
                          className="btn-primary text-sm"
                        >
                          {revision.actionLabel}
                        </button>
                      </div>
                    </div>
                  )}

                  {expandedId === item.id && (
                    <div className="mt-4 pt-4 border-t border-gray-border">
                      <div className="grid gap-3 text-sm text-gray-text md:grid-cols-3">
                        <span>Current URL: {publicPath}</span>
                        {item.route_version && <span>Version: {item.route_version}</span>}
                        {item.published_at && (
                          <span>Published: {new Date(item.published_at).toLocaleString()}</span>
                        )}
                      </div>
                      <pre className="mt-3 bg-dark-lighter p-4 rounded-lg text-sm text-white font-mono overflow-x-auto">
                        {JSON.stringify(item.fields || {}, null, 2)}
                      </pre>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {showCreateModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm animate-fade-in">
          <div className="glass-card w-full max-w-4xl max-h-[90vh] overflow-y-auto animate-spring-in">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-xl font-semibold text-white">Create Content Route</h3>
                <button
                  onClick={() => setShowCreateModal(false)}
                  className="text-gray-text hover:text-white"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="space-y-4">
                <div className="grid gap-4 md:grid-cols-2">
                  <div>
                    <label className="block text-sm font-medium text-gray-text mb-1">
                      Display Name
                    </label>
                    <input
                      value={formDisplayName}
                      onChange={event => setFormDisplayName(event.target.value)}
                      className="input w-full"
                      placeholder="Tax Info"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-text mb-1">
                      Content Type
                    </label>
                    <input
                      list="content-type-presets"
                      value={formType}
                      onChange={event => selectPreset(event.target.value)}
                      onBlur={() => setFormType(normalizeContentType(formType))}
                      className="input w-full font-mono"
                      placeholder="TAX_INFO"
                    />
                    <datalist id="content-type-presets">
                      {contentTypes.map(contentType => (
                        <option key={contentType} value={contentType} />
                      ))}
                    </datalist>
                  </div>
                </div>

                <div className="grid gap-4 md:grid-cols-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-text mb-1">
                      Public Path
                    </label>
                    <input
                      value={formRoutePath}
                      onChange={event => setFormRoutePath(event.target.value)}
                      onBlur={() => setFormRoutePath(normalizeRoutePath(formRoutePath))}
                      className="input w-full font-mono"
                      placeholder="tax-info"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-text mb-1">Locale</label>
                    <select
                      value={formLocale}
                      onChange={event => setFormLocale(event.target.value)}
                      className="input w-full"
                    >
                      <option value="en">English</option>
                      <option value="es">Spanish</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-text mb-1">Mode</label>
                    <select
                      value={formMode}
                      onChange={event => setFormMode(event.target.value as RouteMode)}
                      className="input w-full"
                    >
                      <option value="CURRENT">Current</option>
                      <option value="VERSIONED">Versioned</option>
                    </select>
                  </div>
                </div>

                {formMode === 'VERSIONED' && (
                  <div className="grid gap-4 md:grid-cols-2">
                    <div>
                      <label className="block text-sm font-medium text-gray-text mb-1">
                        Version
                      </label>
                      <input
                        value={formRouteVersion}
                        onChange={event => setFormRouteVersion(event.target.value)}
                        className="input w-full font-mono"
                        placeholder="2025"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-text mb-1">
                        Version Query Params
                      </label>
                      <input
                        value={formVersionQueryParams}
                        onChange={event => setFormVersionQueryParams(event.target.value)}
                        className="input w-full font-mono"
                        placeholder="version, year"
                      />
                    </div>
                  </div>
                )}

                <div className="rounded-xl border border-gray-border bg-dark-lighter px-4 py-3 text-sm text-gray-text">
                  <span className="font-mono text-white">{createUrl}</span>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-text mb-1">
                    Payload JSON
                  </label>
                  <textarea
                    value={formFields}
                    onChange={event => setFormFields(event.target.value)}
                    className="input w-full font-mono text-sm"
                    rows={14}
                    placeholder='{ "title": "Welcome" }'
                  />
                </div>

                {formError && <div className="text-sm text-red-400">{formError}</div>}

                <div className="flex items-center justify-end space-x-3 pt-4">
                  <button onClick={() => setShowCreateModal(false)} className="btn-secondary">
                    Cancel
                  </button>
                  <button
                    onClick={handleCreate}
                    disabled={createMutation.isPending || !createRoutePath}
                    className="btn-primary disabled:opacity-50"
                  >
                    Create Route
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {editingItem && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm animate-fade-in">
          <div className="glass-card w-full max-w-4xl max-h-[90vh] overflow-y-auto animate-spring-in">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-xl font-semibold text-white">
                  {editingItem.status === 'PUBLISHED' ? 'Propose Revision' : 'Edit Route'}
                </h3>
                <button
                  onClick={() => setEditingItem(null)}
                  className="text-gray-text hover:text-white"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="space-y-4">
                {editingItem.status === 'PUBLISHED' && (
                  <div className="rounded-lg border border-gray-border bg-dark-lighter px-4 py-3 text-sm text-gray-text">
                    {editingRevisionNote ??
                      'This route is live, so it is not edited in place. Saving stores your edit on the server as a proposed revision and sends it for approval; the live route does not change until a second operator approves it and you publish it.'}
                  </div>
                )}
                <div className="grid gap-4 md:grid-cols-2">
                  <div>
                    <label className="block text-sm font-medium text-gray-text mb-1">
                      Display Name
                    </label>
                    <input
                      value={editDisplayName}
                      onChange={event => setEditDisplayName(event.target.value)}
                      className="input w-full"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-text mb-1">
                      Public Path
                    </label>
                    <input
                      value={editRoutePath}
                      onChange={event => setEditRoutePath(event.target.value)}
                      onBlur={() => setEditRoutePath(normalizeRoutePath(editRoutePath))}
                      className="input w-full font-mono"
                    />
                  </div>
                </div>

                <div className="grid gap-4 md:grid-cols-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-text mb-1">Mode</label>
                    <select
                      value={editMode}
                      onChange={event => setEditMode(event.target.value as RouteMode)}
                      className="input w-full"
                    >
                      <option value="CURRENT">Current</option>
                      <option value="VERSIONED">Versioned</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-text mb-1">Version</label>
                    <input
                      value={editRouteVersion}
                      onChange={event => setEditRouteVersion(event.target.value)}
                      disabled={editMode !== 'VERSIONED'}
                      className="input w-full font-mono disabled:opacity-50"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-text mb-1">
                      Version Query Params
                    </label>
                    <input
                      value={editVersionQueryParams}
                      onChange={event => setEditVersionQueryParams(event.target.value)}
                      disabled={editMode !== 'VERSIONED'}
                      className="input w-full font-mono disabled:opacity-50"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-text mb-1">
                    Payload JSON
                  </label>
                  <textarea
                    value={editFields}
                    onChange={event => setEditFields(event.target.value)}
                    className="input w-full font-mono text-sm"
                    rows={14}
                  />
                </div>

                {formError && <div className="text-sm text-red-400">{formError}</div>}

                <div className="flex items-center justify-between pt-4">
                  <div className="text-sm text-gray-text flex items-center space-x-2">
                    <FileJson className="w-4 h-4" />
                    <span className="font-mono">/content/{normalizeRoutePath(editRoutePath)}</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <button onClick={() => setEditingItem(null)} className="btn-secondary">
                      Cancel
                    </button>
                    <button
                      onClick={handleUpdate}
                      disabled={updateMutation.isPending}
                      className="btn-primary disabled:opacity-50"
                    >
                      {editingItem.status === 'PUBLISHED' ? 'Propose revision' : 'Save Changes'}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {approvalAction && (
        <ApprovalRequestModal
          operation={approvalAction.operation}
          targetId={approvalAction.targetId}
          targetLabel={approvalAction.targetLabel}
          title={approvalAction.title}
          description={approvalAction.description}
          execute={approvalAction.execute}
          openRequest={approvalAction.openRequest}
          resumeRequestId={approvalAction.resumeRequestId}
          stale={approvalAction.stale}
          onRevise={approvalAction.onRevise}
          onClose={() => {
            setApprovalAction(null);
            refreshRevisions();
          }}
          onCompleted={() => {
            queryClient.invalidateQueries({ queryKey: ['content'] });
            setEditingItem(null);
            setFormError('');
            toast.success(`${approvalAction.title}: done`);
          }}
        />
      )}
    </div>
  );
}
